import { ART_GROUP_BASE, artGroupOf } from '../sorcery/artGroups';

// A collection row's identity below the card: which illustration, in which
// finish. The two travel together everywhere. A card's Welcome Kit foil is
// as distinct from its Welcome Kit standard as it is from its booster foil.
export function variantKey(artGroup, foiling) {
  return `${artGroup || ART_GROUP_BASE}|${foiling || 'S'}`;
}

// Total copies a player owns of a given card, summed across every art and
// finish. Used for "do I own at least one copy of this card?" checks where
// neither distinction matters (e.g. deck legality, base ownership filter for
// the deck builder). And for the per-deck copy cap, which is card-wide: four
// Apprentice Wizards means four across booster, foil and Welcome Kit alike.
export function buildOwnedMap(collection) {
  const owned = new Map();
  if (!Array.isArray(collection)) return owned;
  for (const entry of collection) {
    if (!entry?.cardId) continue;
    owned.set(entry.cardId, (owned.get(entry.cardId) || 0) + (entry.quantity || 0));
  }
  return owned;
}

// Per-variant ownership map: cardId -> Map<variantKey, qty>. Used by the
// deck builder and auction house to render each art x finish as its own tile.
export function buildVariantOwnedMap(collection) {
  const map = new Map();
  if (!Array.isArray(collection)) return map;
  for (const entry of collection) {
    if (!entry?.cardId) continue;
    const key = variantKey(entry.artGroup, entry.foiling);
    let inner = map.get(entry.cardId);
    if (!inner) {
      inner = new Map();
      map.set(entry.cardId, inner);
    }
    inner.set(key, (inner.get(key) || 0) + (entry.quantity || 0));
  }
  return map;
}

export function getOwnedQty(variantMap, cardId, artGroup = ART_GROUP_BASE, foiling = 'S') {
  return variantMap.get(cardId)?.get(variantKey(artGroup, foiling)) || 0;
}

// Cards used in saved decks. Aggregated by (cardId, foiling) so the
// auction house can tell that listing a foil copy is allowed even when
// the standard copy is in a deck. Falls back to foiling 'S' for legacy
// deck entries that pre-date the foiling field.
export function buildUsedMap(decks, excludeDeckId) {
  const used = new Map();
  if (!Array.isArray(decks)) return used;
  for (const deck of decks) {
    if (deck.id === excludeDeckId) continue;
    if (!Array.isArray(deck.cards)) continue;
    for (const card of deck.cards) {
      if (!card?.cardId) continue;
      used.set(card.cardId, (used.get(card.cardId) || 0) + 1);
    }
  }
  return used;
}

// Per-(cardId, art group, foiling) used map. Both axes are derived from the
// deck entry's printing when it has one. Saved decks store printingId, so a
// deck built before art groups existed still resolves to the right art. Used
// by the sell tab so a foil (or an alt art) can be listed even when another
// version of the same card is in use.
export function buildUsedVariantMap(decks, excludeDeckId) {
  const used = new Map();
  if (!Array.isArray(decks)) return used;
  for (const deck of decks) {
    if (deck.id === excludeDeckId) continue;
    if (!Array.isArray(deck.cards)) continue;
    for (const card of deck.cards) {
      if (!card?.cardId) continue;
      const key = variantKey(artGroupOf(card.printingId), card.foiling);
      let inner = used.get(card.cardId);
      if (!inner) {
        inner = new Map();
        used.set(card.cardId, inner);
      }
      inner.set(key, (inner.get(key) || 0) + 1);
    }
  }
  return used;
}

export function getUsedQty(usedVariantMap, cardId, artGroup = ART_GROUP_BASE, foiling = 'S') {
  return usedVariantMap.get(cardId)?.get(variantKey(artGroup, foiling)) || 0;
}

export function getAvailableQuantity(cardId, ownedMap, usedMap) {
  const owned = ownedMap.get(cardId) || 0;
  const used = usedMap.get(cardId) || 0;
  return Math.max(0, owned - used);
}

export function getAvailableQtyForVariant(cardId, artGroup, foiling, variantOwnedMap, usedVariantMap) {
  const owned = getOwnedQty(variantOwnedMap, cardId, artGroup, foiling);
  const used = getUsedQty(usedVariantMap, cardId, artGroup, foiling);
  return Math.max(0, owned - used);
}

export function filterCollectionCards(sorceryCards, ownedMap) {
  return sorceryCards.filter((card) => (ownedMap.get(card.unique_id) || 0) > 0);
}

export function countInDeck(cardId, deckCards) {
  return deckCards.filter((c) => c.cardId === cardId).length;
}
